import { useEffect, useRef, useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Brand } from "@/components/Brand";
import SeoHead from "@/components/SeoHead";
import PublicFooter from "@/components/PublicFooter";
import {
  ArrowRight, CheckCircle2, Info, ShieldCheck, ExternalLink,
} from "lucide-react";

const usePrefersReducedMotion = () => {
  const [rm, setRm] = useState(false);
  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    setRm(m.matches);
    const cb = (e) => setRm(e.matches);
    m.addEventListener?.("change", cb);
    return () => m.removeEventListener?.("change", cb);
  }, []);
  return rm;
};

const useReveal = (opts = {}) => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setSeen(true); io.disconnect(); }
    }, { threshold: opts.threshold ?? 0.2, rootMargin: opts.rootMargin ?? "0px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [opts.threshold, opts.rootMargin]);
  return [ref, seen];
};

const useCountUp = (target, active, duration = 1400) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active || target == null) return;
    const rm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (rm) { setN(target); return; }
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return n;
};

const T = {
  en: {
    navFind: "Find housing help",
    navProvider: "For property owners",
    signIn: "Sign in",
    heroFind: "Find housing help",
    heroProvider: "For property owners",
    disconnectFindLink: "Find housing help",
    disconnectProviderLink: "For property owners",
    liveNumbersLedeScoped: "Deepest verification in Greater San Antonio: Bexar, Comal, Guadalupe, Kendall. Other Texas metros are still in research and are not shown yet.",
    liveNumbersLedeAll: "Deepest verification in Greater San Antonio: Bexar, Comal, Guadalupe, Kendall. Other Texas metros are included and labeled by how well we have verified them.",
  },
  es: {
    navFind: "Buscar ayuda con vivienda",
    navProvider: "Para propietarios",
    signIn: "Iniciar sesión",
    heroFind: "Buscar ayuda con vivienda",
    heroProvider: "Para propietarios",
    disconnectFindLink: "Buscar ayuda con vivienda",
    disconnectProviderLink: "Para propietarios",
    liveNumbersLedeScoped: "Verificación más profunda en Greater San Antonio: Bexar, Comal, Guadalupe, Kendall. Otras áreas metropolitanas de Texas todavía están en investigación y aún no se muestran.",
    liveNumbersLedeAll: "Verificación más profunda en Greater San Antonio: Bexar, Comal, Guadalupe, Kendall. Otras áreas metropolitanas de Texas están incluidas y etiquetadas según qué tan bien las hemos verificado.",
  },
};

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const rm = usePrefersReducedMotion();
  const [coverage, setCoverage] = useState(null);

  const isEs = location.pathname === "/es" || location.pathname.startsWith("/es/");
  const t = isEs ? T.es : T.en;
  const findHref = isEs ? "/es/find" : "/find";
  const providerHref = isEs ? "/es/provider" : "/provider";

  useEffect(() => {
    document.title = "Throughline · Housing Opportunity Infrastructure";
    fetch(`${API}/public/coverage`).then(r => r.json()).then(setCoverage).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAF9] text-slate-900 antialiased" data-testid="landing-page">
      <SeoHead
        title="Throughline · Housing Opportunity Infrastructure · Greater San Antonio"
        description="Throughline connects fragmented housing demand to vetted real estate. An evidence-forward operating layer for housing programs, funders, operators, and property partners in Greater San Antonio."
        canonicalPath="/"
        hreflang={[{ lang: "en", path: "/" }, { lang: "x-default", path: "/" }]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Throughline",
          url: "https://throughlinehousing.com/",
          description: "Housing Opportunity Infrastructure for Greater San Antonio — connects vetted real estate to documented housing demand.",
          areaServed: { "@type": "AdministrativeArea", name: "Greater San Antonio, TX" },
        }}
      />
      <style>{`
        @keyframes th-fragment {
          0% { transform: translate3d(0,0,0) rotate(0); opacity: 1; }
          40% { transform: translate3d(var(--dx),var(--dy),0) rotate(var(--dr)); opacity: 1; }
          100% { transform: translate3d(0,0,0) rotate(0); opacity: 1; }
        }
        @keyframes th-fade-in { from { opacity: 0.001; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        @keyframes th-line-draw { from { stroke-dashoffset: 400; } to { stroke-dashoffset: 0; } }
        @keyframes th-value-travel { 0% { offset-distance: 0%; opacity: 0; } 15% { opacity: 1; } 85% { opacity: 1; } 100% { offset-distance: 100%; opacity: 0; } }
        @media (prefers-reduced-motion: reduce) {
          .th-anim, .th-anim * { animation: none !important; transition: none !important; }
        }
        .grain {
          background-image: radial-gradient(rgba(15,23,42,0.045) 1px, transparent 1px);
          background-size: 3px 3px;
        }
        .th-frag-char { display: inline-block; animation: th-fragment 1.2s cubic-bezier(0.7,0,0.2,1) both; }
        .th-appear { animation: th-fade-in 700ms cubic-bezier(0.2,0.7,0.2,1) 0.9s both; }
        .th-underline { position: relative; }
        .th-underline::after { content:""; position:absolute; left:0; bottom:-2px; width:100%; height:1px; background:currentColor; transform: scaleX(0); transform-origin: right; transition: transform 400ms cubic-bezier(0.7,0,0.2,1); }
        .th-underline:hover::after { transform: scaleX(1); transform-origin: left; }
        .th-magnet { transition: transform 300ms cubic-bezier(0.2,0.7,0.2,1); }
        .th-magnet:hover { transform: translateY(-1px); }
      `}</style>

      {/* NAV */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 min-h-16 py-2 flex items-center justify-between gap-3">
          <Brand variant="dark" size={20} />
          <nav className="flex items-center flex-wrap justify-end gap-x-3 gap-y-0 sm:gap-6" aria-label="Primary">
            <Link to={findHref} className="text-[13px] sm:text-sm font-medium text-slate-900 th-underline th-magnet whitespace-nowrap" data-testid="landing-nav-find">{t.navFind}</Link>
            <Link to={providerHref} className="text-[13px] sm:text-sm font-medium text-slate-900 th-underline th-magnet whitespace-nowrap" data-testid="landing-nav-provider">{t.navProvider}</Link>
            <span className="hidden sm:inline text-slate-300" aria-hidden="true">·</span>
            <Link to="/login" className="text-xs text-slate-500 hover:text-slate-900" data-testid="landing-signin-link">{t.signIn}</Link>
          </nav>
        </div>
      </header>

      {/* HERO — kinetic thesis */}
      <section className="relative bg-slate-950 text-white overflow-hidden">
        <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-24 pb-24 lg:pt-36 lg:pb-32">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-semibold mb-8 th-anim th-appear">Housing Opportunity Infrastructure</div>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold leading-[0.98] tracking-[-0.03em] max-w-5xl th-anim">
            <span className="block">
              Connect{" "}
              <span aria-label="fragmented" className="inline-block">
                {rm ? <span>fragmented</span> : (
                  "fragmented".split("").map((c, i) => (
                    <span key={i} className="th-frag-char" style={{
                      "--dx": `${(i % 2 ? 1 : -1) * (6 + (i * 3))}px`,
                      "--dy": `${(i % 3 - 1) * 4}px`,
                      "--dr": `${(i % 2 ? 1 : -1) * 3}deg`,
                      animationDelay: `${i * 40}ms`,
                    }}>{c}</span>
                  ))
                )}
              </span>
            </span>
            <span className="th-appear block">
              housing demand to <span className="text-[#E88C3D]">vetted real estate.</span>
            </span>
          </h1>
          <p className="mt-8 text-lg lg:text-xl text-slate-300 max-w-3xl leading-relaxed th-appear" style={{ animationDelay: "1.2s" }}>
            The housing need exists. The funding exists. Suitable real estate exists.
            <br className="hidden sm:inline" />The information connecting them is fragmented. Throughline is the operating layer that closes that gap.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4 th-appear" style={{ animationDelay: "1.35s" }}>
            <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-slate-100 h-12 px-7 th-magnet" data-testid="landing-hero-provider">
              <Link to={providerHref}>
                {t.heroProvider} <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-slate-700 bg-transparent text-white hover:bg-white hover:text-slate-900 h-12 px-7 th-magnet" data-testid="landing-hero-find">
              <Link to={findHref}>
                {t.heroFind} <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
            <Link to={user ? "/dashboard" : "/login"} className="text-sm text-slate-400 hover:text-white th-underline ml-1" data-testid="landing-hero-signin">
              {user ? "Open workspace" : t.signIn}
            </Link>
          </div>
        </div>
      </section>

      {/* THE DISCONNECT */}
      <Disconnect rm={rm} findHref={findHref} providerHref={providerHref} t={t} />

      {/* VALUE CHAIN — pinned pipeline */}
      <ValueChain rm={rm} />

      {/* EVIDENCE DISCIPLINE */}
      <EvidenceDiscipline rm={rm} />

      {/* PAYMENT FLOW */}
      <PaymentFlow rm={rm} />

      {/* LIVE NUMBERS */}
      <LiveNumbers coverage={coverage} rm={rm} t={t} />

      {/* REQUEST ACCESS */}
      <section id="request-access" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28" data-testid="landing-access-form">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">Request access</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 tracking-[-0.02em] leading-[1.1] break-words">See if Throughline fits your work.</h2>
            <p className="mt-5 text-sm text-slate-700 leading-relaxed max-w-lg">
              Access is granted to vetted operators, agencies, funders, referral partners and property partners active in a covered market. Tell us who you are and what you're working on. We reply personally.
            </p>
          </div>
          <AccessRequestForm />
        </div>
      </section>

      {/* COMPLIANCE */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pb-20">
        <div className="rounded-lg bg-white border border-slate-200 p-8 lg:p-10">
          <ShieldCheck className="w-6 h-6 text-slate-500 mb-4" />
          <h3 className="text-xl font-semibold tracking-[-0.01em]">Informational infrastructure. Not investment advice.</h3>
          <p className="text-slate-600 mt-3 max-w-3xl text-sm leading-relaxed">
            Throughline provides property, housing-program, operating, financial-modeling and performance information. It does not recommend investments, determine suitability, guarantee results, accept or custody funds, or execute securities transactions. Every match, report and underwriting scenario is evidence-linked and distinguishes verified facts from projections.
          </p>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-6 text-xs text-slate-500 flex flex-col md:flex-row justify-between gap-2">
          <div>Informational only · Not investment advice · Evidence-forward</div>
        </div>
      </footer>
      <PublicFooter lang="en" />
    </div>
  );
}

/* ---------------- Sections ---------------- */

function Disconnect({ rm, findHref, providerHref, t }) {
  const [ref, seen] = useReveal({ threshold: 0.35 });
  return (
    <section ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-24 lg:py-32" data-testid="landing-disconnect">
      <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">The disconnect</div>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 tracking-[-0.02em] leading-[1.1] break-words max-w-3xl">Two sides that rarely see each other clearly.</h2>

      <div className="relative mt-16 grid md:grid-cols-2 gap-6 lg:gap-24">
        {/* Line SVG between columns — draws on reveal */}
        <svg className="hidden md:block absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 pointer-events-none" width="120" height="120" viewBox="0 0 120 120" aria-hidden="true">
          <line x1="0" y1="60" x2="120" y2="60" stroke="#0F172A" strokeWidth="1.5"
                strokeDasharray="400" strokeDashoffset={rm ? 0 : (seen ? 0 : 400)}
                style={{ transition: rm ? "none" : "stroke-dashoffset 1.2s cubic-bezier(0.7,0,0.2,1) 0.2s" }} />
          <circle cx="60" cy="60" r="4" fill="#0F172A" opacity={seen || rm ? 1 : 0}
                  style={{ transition: rm ? "none" : "opacity 400ms ease 1.2s" }} />
        </svg>

        <div className={`th-anim ${!rm ? "transition-all duration-700" : ""}`} style={{ opacity: seen || rm ? 1 : 0, transform: seen || rm ? "none" : "translateX(-12px)" }}>
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">Side A</div>
          <h3 className="text-2xl font-semibold mt-2 tracking-[-0.01em]">Funded &amp; referral-driven housing demand</h3>
          <p className="text-sm text-slate-700 mt-3 leading-relaxed">
            Government agencies, housing authorities, nonprofits, healthcare and behavioral-health organizations, veteran and reentry programs. They carry vouchers, contracts, waitlists, and placement obligations. Demand is real. Matching it to physical real estate is the bottleneck.
          </p>
          <Link to={findHref} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 th-underline th-magnet" data-testid="disconnect-side-a-cta">
            {t.disconnectFindLink} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className={`th-anim ${!rm ? "transition-all duration-700 delay-300" : ""}`} style={{ opacity: seen || rm ? 1 : 0, transform: seen || rm ? "none" : "translateX(12px)" }}>
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">Side B</div>
          <h3 className="text-2xl font-semibold mt-2 tracking-[-0.01em]">Property owners &amp; investors</h3>
          <p className="text-sm text-slate-700 mt-3 leading-relaxed">
            Own or identify real estate that is vacant, under-performing conventionally, or potentially suitable for specialized housing. They lack an efficient way to understand the programs, funding, demand, regulatory requirements and partners needed to deploy it responsibly.
          </p>
          <Link to={providerHref} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 th-underline th-magnet" data-testid="disconnect-side-b-cta">
            {t.disconnectProviderLink} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <div className="mt-16 text-sm text-slate-600 max-w-3xl leading-relaxed">
        Properties enter Throughline <span className="font-semibold text-slate-900">intentionally</span> and are vetted for fit. Throughline is not an MLS scraper and does not evaluate every listing on the market.
      </div>
    </section>
  );
}

const CHAIN_NODES = [
  { k: "need",       t: "Housing Need",              d: "Who needs housing, defined by real programs and populations — not abstractions." },
  { k: "funding",    t: "Funding / Payment",         d: "How dollars flow — vouchers, contracts, per-unit, per-bed, per-diem, appropriations." },
  { k: "referral",   t: "Referral Demand",           d: "Which agency sends whom, at what velocity, under what waitlist reality." },
  { k: "operators",  t: "Operators / Partners",      d: "Who runs the property under a program's licensing, service, and compliance rules." },
  { k: "property",   t: "Vetted Real Estate",        d: "Properties intentionally submitted, evidence-tagged, and matched — never scraped." },
  { k: "execution",  t: "Execution",                 d: "Acquisition, conversion, compliance, referral activation, placement, stabilization." },
  { k: "performance",t: "Measured Performance",      d: "Occupancy, placements, actuals vs projections — transparent, not advisory." },
];

const useIsMobile = () => {
  const [m, setM] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const cb = (e) => setM(e.matches);
    mq.addEventListener?.("change", cb);
    return () => mq.removeEventListener?.("change", cb);
  }, []);
  return m;
};

function ValueChain({ rm }) {
  const wrapRef = useRef(null);
  const isMobile = useIsMobile();
  const pinned = !rm && !isMobile;
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!pinned) { setProgress(1); return; }
    const el = wrapRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      raf = raf || requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const total = el.offsetHeight - window.innerHeight;
        const scrolled = Math.max(0, Math.min(total, -rect.top));
        setProgress(total > 0 ? scrolled / total : 0);
        raf = 0;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [pinned]);

  const activeIndex = Math.min(CHAIN_NODES.length - 1, Math.floor(progress * CHAIN_NODES.length));

  return (
    <section className="bg-slate-950 text-white overflow-x-clip" data-testid="landing-value-chain">
      <div ref={wrapRef} className="relative" style={{ height: pinned ? `${CHAIN_NODES.length * 60}vh` : "auto" }}>
        <div className={pinned ? "sticky top-0 h-screen flex items-center overflow-hidden" : "relative"}>
          <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
          <div className="relative w-full max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 py-14">
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-semibold">How the connection is made</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 tracking-[-0.02em] leading-[1.1] max-w-4xl break-words" data-testid="value-chain-heading">
              Seven links. One&nbsp;decision&nbsp;line.
            </h2>

            {/* Desktop horizontal pipeline */}
            <div className="hidden md:block mt-14">
              <div className="relative">
                <div className="h-px w-full bg-slate-800" />
                <div className="h-px bg-[#E88C3D] absolute left-0 top-0 transition-[width] duration-500 ease-out" style={{ width: `${((activeIndex + 1) / CHAIN_NODES.length) * 100}%` }} />
                <div className="grid grid-cols-7 gap-3 mt-6">
                  {CHAIN_NODES.map((n, i) => {
                    const isActive = i <= activeIndex;
                    return (
                      <div key={n.k} className="text-left" data-testid={`value-stage-${i+1}`}>
                        <div className={`w-3 h-3 rounded-full -mt-8 ml-0 mb-4 border ${isActive ? "bg-[#E88C3D] border-[#E88C3D]" : "bg-slate-950 border-slate-700"}`}
                             style={{ transition: rm ? "none" : "background-color 300ms, border-color 300ms" }} />
                        <div className={`text-[9px] uppercase tracking-widest font-semibold ${isActive ? "text-[#E88C3D]" : "text-slate-500"}`}>Stage {i+1}</div>
                        <div className={`text-sm font-semibold mt-1 leading-tight tracking-[-0.005em] ${isActive ? "text-white" : "text-slate-500"}`}>{n.t}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="mt-10 min-h-[80px] max-w-3xl">
                <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-semibold">{`0${activeIndex+1}`.slice(-2)} · {CHAIN_NODES[activeIndex].t}</div>
                <div className="text-2xl lg:text-3xl font-semibold mt-2 tracking-[-0.01em] leading-snug text-white">{CHAIN_NODES[activeIndex].d}</div>
              </div>
            </div>

            {/* Mobile vertical stepped — plain document flow, reveal on enter */}
            <div className="md:hidden mt-10 space-y-6">
              {CHAIN_NODES.map((n, i) => (
                <div key={n.k} data-testid={`value-stage-${i+1}`}><MobileChainStep n={n} i={i} rm={rm} /></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MobileChainStep({ n, i, rm }) {
  const [ref, seen] = useReveal({ threshold: 0.25, rootMargin: "0px 0px -10% 0px" });
  const on = seen || rm;
  return (
    <div ref={ref} className={`border-l pl-4 ${on ? "border-[#E88C3D]" : "border-slate-800"}`}
         style={{ opacity: on ? 1 : 0.35, transform: on || rm ? "none" : "translateY(10px)", transition: rm ? "none" : "opacity 450ms ease, transform 450ms ease, border-color 450ms ease" }}>
      <div className={`text-[9px] uppercase tracking-widest font-semibold ${on ? "text-[#E88C3D]" : "text-slate-400"}`}>Stage {i+1}</div>
      <div className="text-lg font-semibold mt-1 text-white">{n.t}</div>
      <div className="text-sm text-slate-400 mt-1">{n.d}</div>
    </div>
  );
}

const GATES = [
  { k: "geo",        l: "County in program geography",         result: "pass" },
  { k: "beds",       l: "Bedroom fit for target population",   result: "pass" },
  { k: "rent",       l: "Specialized rent ≥ conventional",     result: "pass" },
  { k: "zoning",     l: "Zoning compatible",                   result: "warn" },
  { k: "intake",     l: "Referral velocity confirmed",         result: "warn" },
  { k: "payment",    l: "Payment amount documented",           result: "warn" },
  { k: "referral",   l: "Referral process confirmed",          result: "warn" },
];
const EV_LEGEND = [
  { l: "Verified Fact",     tone: "bg-emerald-500" },
  { l: "Partner Confirmed", tone: "bg-emerald-400" },
  { l: "Public Source",     tone: "bg-sky-400" },
  { l: "Estimate",          tone: "bg-amber-400" },
  { l: "Assumption",        tone: "bg-amber-300" },
  { l: "Needs Verification",tone: "bg-rose-400" },
];

function EvidenceDiscipline({ rm }) {
  const [ref, seen] = useReveal({ threshold: 0.35 });
  const opp = useCountUp(91, seen);
  const conf = useCountUp(37, seen);
  return (
    <section ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-24 lg:py-32" data-testid="landing-evidence">
      <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">The evidence discipline</div>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 tracking-[-0.02em] leading-[1.1] break-words max-w-3xl">Two scores. Never one number.</h2>
      <p className="mt-5 text-sm text-slate-700 leading-relaxed max-w-2xl">
        Every match runs through seven qualification gates. The output is <em>always</em> two scores: an <strong>Opportunity Score</strong> for how good the deal is, and a <strong>Confidence Score</strong> for how strong the evidence is. High potential does not mean verified — and we will always tell you which is which.
      </p>

      <div className="mt-14 grid lg:grid-cols-[1fr_auto] gap-10 lg:gap-16 items-start">
        {/* Gate evaluation */}
        <div className="space-y-2" data-testid="evidence-gates">
          {GATES.map((g, i) => (
            <GateRow key={g.k} g={g} i={i} active={seen} rm={rm} />
          ))}
        </div>

        {/* Score counters */}
        <div className="flex gap-6" data-testid="evidence-scores">
          <ScoreDial label="Opportunity" value={opp} accent="#C25E00" />
          <ScoreDial label="Confidence" value={conf} accent="#0F172A" />
        </div>
      </div>

      {/* Evidence legend */}
      <div className="mt-16 pt-10 border-t border-slate-200">
        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold mb-4">Six evidence levels — applied to every claim</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {EV_LEGEND.map((e, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-slate-700">
              <span className={`w-2 h-2 rounded-full ${e.tone}`} />{e.l}
            </div>
          ))}
        </div>
        <div className="mt-6 text-sm text-slate-600 italic max-w-2xl">
          A 91 that no one has verified is not the same as a 91 confirmed by a housing authority. Throughline never conflates them.
        </div>
      </div>
    </section>
  );
}

function GateRow({ g, i, active, rm }) {
  const delay = rm ? 0 : 120 + i * 160;
  const [show, setShow] = useState(false);
  useEffect(() => { if (!active) return; const t = setTimeout(() => setShow(true), delay); return () => clearTimeout(t); }, [active, delay]);
  const tone = g.result === "pass" ? "border-emerald-300 bg-emerald-50/40 text-emerald-800" : "border-amber-300 bg-amber-50/40 text-amber-800";
  const symbol = g.result === "pass" ? "✓" : "⚠";
  return (
    <div className={`flex items-center justify-between border rounded-md px-3 py-2 text-sm ${tone}`}
         style={{ opacity: show || rm ? 1 : 0, transform: show || rm ? "none" : "translateX(-8px)", transition: rm ? "none" : "opacity 400ms cubic-bezier(0.2,0.7,0.2,1), transform 400ms cubic-bezier(0.2,0.7,0.2,1)" }}>
      <span className="text-slate-800">{g.l}</span>
      <span className="font-mono text-xs">{symbol}</span>
    </div>
  );
}

function ScoreDial({ label, value, accent }) {
  const pct = Math.max(0, Math.min(100, value));
  const r = 42;
  const c = 2 * Math.PI * r;
  const off = c * (1 - pct / 100);
  return (
    <div className="text-center">
      <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-semibold mb-2">{label}</div>
      <div className="relative w-32 h-32">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r={r} stroke="#E2E8F0" strokeWidth="6" fill="none" />
          <circle cx="50" cy="50" r={r} stroke={accent} strokeWidth="6" fill="none"
                  strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
                  style={{ transition: "stroke-dashoffset 900ms cubic-bezier(0.2,0.7,0.2,1)" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-4xl font-bold font-mono tracking-tight" style={{ color: accent }}>{value}</div>
          <div className="text-[10px] text-slate-400 -mt-1 font-mono">/100</div>
        </div>
      </div>
    </div>
  );
}

const FLOW_NODES = [
  { l: "Funding Source",    tone: "verified" },
  { l: "Program / Agency",  tone: "verified" },
  { l: "Operator / Referral", tone: "assumed" },
  { l: "Resident Placement", tone: "assumed" },
  { l: "Property",           tone: "verified" },
  { l: "Payment",            tone: "verified" },
];

function PaymentFlow({ rm }) {
  const [ref, seen] = useReveal({ threshold: 0.45 });
  return (
    <section ref={ref} className="bg-slate-100 border-y border-slate-200" data-testid="landing-payment-flow">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-24 lg:py-32">
        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">The payment flow</div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 tracking-[-0.02em] leading-[1.1] max-w-3xl break-words">Most people evaluating this have never seen the money path drawn.</h2>

        <div className="mt-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3 relative">
            {/* Traveling line */}
            <svg className="hidden md:block absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              <line x1="0" y1="50" x2="100" y2="50" stroke="#0F172A" strokeWidth="0.4" strokeDasharray="1200"
                    strokeDashoffset={rm ? 0 : (seen ? 0 : 1200)}
                    style={{ transition: rm ? "none" : "stroke-dashoffset 1.6s cubic-bezier(0.5,0,0.5,1)" }} />
            </svg>
            {FLOW_NODES.map((n, i) => (
              <div key={i} className="relative z-10 bg-white border border-slate-200 rounded-md p-4 shadow-sm"
                   style={{ opacity: seen || rm ? 1 : 0, transform: seen || rm ? "none" : "translateY(6px)", transition: rm ? "none" : `opacity 500ms ease ${100 + i*140}ms, transform 500ms ease ${100 + i*140}ms` }}>
                <div className={`text-[9px] uppercase tracking-widest font-semibold ${n.tone === "verified" ? "text-emerald-700" : "text-amber-700"}`}>{n.tone === "verified" ? "Verified" : "Assumed"}</div>
                <div className="text-sm font-semibold mt-1 tracking-[-0.005em]">{n.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex items-center gap-6 text-xs text-slate-600">
          <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Typically verified</div>
          <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" /> Typically assumed</div>
        </div>
      </div>
    </section>
  );
}

function LiveNumbers({ coverage, rm, t }) {
  const [ref, seen] = useReveal({ threshold: 0.5 });
  const tiles = useMemo(() => coverage ? [
    { l: "Programs tracked",       v: coverage.programs_tracked },
    { l: "Counties covered",       v: coverage.counties_covered },
    { l: "Public source citations", v: coverage.public_sources },
    { l: "Verified property files", v: coverage.verified_property_files },
  ].filter(t => t.v > 0) : [], [coverage]);
  const showAll = !!(coverage && coverage.show_research_preview_markets);
  const lede = showAll ? t.liveNumbersLedeAll : t.liveNumbersLedeScoped;
  return (
    <section ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-24 lg:py-32" data-testid="landing-live-numbers">
      <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">On the ground</div>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 tracking-[-0.02em] leading-[1.1] break-words max-w-3xl">Where Throughline is right now.</h2>
      <p className="mt-5 text-sm text-slate-700 max-w-2xl leading-relaxed" data-testid="landing-live-numbers-lede">
        {lede}
      </p>
      {tiles.length > 0 ? (
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3">
          {tiles.map((t, i) => <NumberTile key={t.l} t={t} seen={seen} rm={rm} delay={i * 140} />)}
        </div>
      ) : (
        <div className="mt-12 text-xs text-slate-500 italic">Live metrics loading…</div>
      )}
    </section>
  );
}

function NumberTile({ t, seen, rm, delay }) {
  const n = useCountUp(t.v, seen);
  return (
    <div className="border-l-2 border-slate-900 pl-4"
         style={{ opacity: seen || rm ? 1 : 0, transform: seen || rm ? "none" : "translateY(6px)", transition: rm ? "none" : `opacity 500ms ease ${delay}ms, transform 500ms ease ${delay}ms` }}>
      <div className="text-5xl lg:text-6xl font-bold font-mono tracking-tighter text-slate-900 tabular-nums">{n}</div>
      <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold mt-1">{t.l}</div>
    </div>
  );
}

function AccessRequestForm() {
  const isEs = useLocation().pathname.startsWith("/es");
  const [form, setForm] = useState({ name: "", organization: "", role_type: "agency", market: "", looking_for: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/access-requests`, { method: "POST", headers: {"Content-Type":"application/json"}, body: JSON.stringify(form) });
      if (!r.ok) throw new Error();
      setSent(true);
      toast.success("Request received.");
    } catch { toast.error("Could not submit — please try again."); }
    finally { setBusy(false); }
  };
  if (sent) return (
    <div className="p-10 rounded-lg border border-emerald-300 bg-emerald-50/40" data-testid="access-form-success">
      <CheckCircle2 className="w-6 h-6 text-emerald-700 mb-3" />
      <div className="text-lg font-semibold text-emerald-900 tracking-[-0.01em]">Thanks — we've received your request.</div>
      <div className="text-sm text-emerald-800 mt-1 leading-relaxed">Someone will follow up shortly. No auto-emails. No drip sequences.</div>
    </div>
  );
  return (
    <form onSubmit={submit} className="p-8 rounded-lg border border-slate-200 bg-white space-y-4" data-testid="access-form">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Name</Label><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} required data-testid="access-name" className="mt-1" /></div>
        <div><Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Email</Label><Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="access-email" className="mt-1" /></div>
      </div>
      <div><Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Organization</Label><Input value={form.organization} onChange={e=>setForm({...form, organization:e.target.value})} data-testid="access-org" className="mt-1" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Role</Label>
          <select value={form.role_type} onChange={e=>setForm({...form, role_type:e.target.value})} className="w-full mt-1 border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="access-role">
            <option value="agency">Agency</option><option value="nonprofit">Nonprofit</option><option value="operator">Operator</option>
            <option value="property_owner">Property owner</option><option value="investor">Investor</option><option value="other">Other</option>
          </select>
        </div>
        <div><Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{isEs ? "Ciudad o código postal" : "City or ZIP"}</Label><Input value={form.market} onChange={e=>setForm({...form, market:e.target.value})} placeholder={isEs ? "p. ej. Cibolo o 78108" : "e.g. Cibolo or 78108"} data-testid="access-market" className="mt-1" /></div>
      </div>
      <div><Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">What are you looking for?</Label><Textarea rows={3} value={form.looking_for} onChange={e=>setForm({...form, looking_for:e.target.value})} placeholder="A few sentences." data-testid="access-looking-for" className="mt-1" /></div>
      <Button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 h-11 th-magnet" data-testid="access-submit">
        {busy ? "Sending…" : "Send request"} <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
      <div className="text-[10px] text-slate-500">We don't sell your info. A person reads this.</div>
      <div className="pt-2 border-t border-slate-100 text-sm">
        <Link to={isEs ? "/es/provider" : "/provider"} className="inline-flex items-center gap-1 font-semibold text-slate-900 th-underline" data-testid="access-owner-link">
          {isEs ? "¿Es propietario? Vea para qué podría calificar su propiedad →" : "Property owner? Check what your property could qualify for →"}
        </Link>
      </div>
    </form>
  );
}
